import { useEffect, useState } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';

export default function CouponManager() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percentage',
    discountAmount: '',
    maxUses: '',
    expiresAt: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await API.get('/coupons/seller/mine');
      setCoupons(res.data.coupons);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.discountAmount) {
      toast.error('Fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await API.patch(`/coupons/${editing.id}`, {
          discountAmount: parseFloat(formData.discountAmount),
          maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
          expiresAt: formData.expiresAt || null,
          isActive: true
        });
        toast.success('Coupon updated!');
      } else {
        await API.post('/coupons', {
          code: formData.code.toUpperCase(),
          discountType: formData.discountType,
          discountAmount: parseFloat(formData.discountAmount),
          maxUses: formData.maxUses ? parseInt(formData.maxUses) : null,
          expiresAt: formData.expiresAt || null
        });
        toast.success('Coupon created!');
      }
      setFormData({ code: '', discountType: 'percentage', discountAmount: '', maxUses: '', expiresAt: '' });
      setEditing(null);
      setShowForm(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditing(coupon);
    setFormData({
      code: coupon.code,
      discountType: coupon.discount_type,
      discountAmount: coupon.discount_amount,
      maxUses: coupon.max_uses || '',
      expiresAt: coupon.expires_at ? coupon.expires_at.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    try {
      await API.delete(`/coupons/${id}`);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (err) {
      toast.error('Failed to delete coupon');
    }
  };

  if (loading) {
    return <div style={{ padding: 20, textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700 }}>🎟️ My Coupons</h3>
        <button
          onClick={() => { setEditing(null); setShowForm(!showForm); setFormData({ code: '', discountType: 'percentage', discountAmount: '', maxUses: '', expiresAt: '' }); }}
          className="btn btn-cherry btn-sm"
        >
          {showForm ? 'Cancel' : '+ New Coupon'}
        </button>
      </div>

      {showForm && (
        <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Coupon Code</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="SAVE10"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  disabled={editing}
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select
                  className="form-select"
                  value={formData.discountType}
                  onChange={e => setFormData({ ...formData, discountType: e.target.value })}
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount ($)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <input
                  className="form-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder={formData.discountType === 'percentage' ? '10' : '5.00'}
                  value={formData.discountAmount}
                  onChange={e => setFormData({ ...formData, discountAmount: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Max Uses</label>
                <input
                  className="form-input"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={formData.maxUses}
                  onChange={e => setFormData({ ...formData, maxUses: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Expires</label>
                <input
                  className="form-input"
                  type="date"
                  value={formData.expiresAt}
                  onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" className="btn btn-cherry" disabled={saving}>
                {saving ? '💾 Saving...' : '✓ Save Coupon'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {coupons.length === 0 ? (
        <div style={{ padding: 32, textAlign: 'center', background: 'var(--cream)', borderRadius: 12 }}>
          <p style={{ color: 'var(--text-soft)', marginBottom: 12 }}>No coupons yet</p>
          <p style={{ fontSize: 12, color: 'var(--text-soft)' }}>Create your first coupon to offer discounts to buyers</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {coupons.map(coupon => {
            const isExpired = coupon.expires_at && new Date(coupon.expires_at) < new Date();
            const isMaxedOut = coupon.max_uses && coupon.times_used >= coupon.max_uses;
            const isInactive = !coupon.is_active || isExpired || isMaxedOut;

            return (
              <div
                key={coupon.id}
                style={{
                  padding: 16,
                  border: '1px solid var(--border)',
                  borderRadius: 12,
                  background: isInactive ? '#F5F5F5' : 'white',
                  opacity: isInactive ? 0.6 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                  <div>
                    <h4 style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, fontFamily: 'monospace' }}>
                      {coupon.code}
                    </h4>
                    <p style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 8 }}>
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_amount}% off` : `$${coupon.discount_amount} off`}
                    </p>
                    {isExpired && <span style={{ fontSize: 11, color: '#F44336', fontWeight: 700 }}>🔴 Expired</span>}
                    {isMaxedOut && <span style={{ fontSize: 11, color: '#F44336', fontWeight: 700 }}>🔴 Max uses reached</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleEdit(coupon)}
                      className="btn btn-sm"
                      style={{ background: 'var(--cream)', border: 'none', fontSize: 12 }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDelete(coupon.id)}
                      className="btn btn-sm"
                      style={{ background: '#FDECEA', color: 'var(--cherry)', border: 'none', fontSize: 12 }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: 12,
                  paddingTop: 12,
                  borderTop: '1px solid var(--border)',
                  fontSize: 12
                }}>
                  <div>
                    <div style={{ color: 'var(--text-soft)', marginBottom: 4 }}>Uses</div>
                    <div style={{ fontWeight: 700 }}>
                      {coupon.times_used}{coupon.max_uses ? `/${coupon.max_uses}` : ''}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-soft)', marginBottom: 4 }}>Total Discount</div>
                    <div style={{ fontWeight: 700 }}>
                      ${parseFloat(coupon.total_discount_given || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--text-soft)', marginBottom: 4 }}>Expires</div>
                    <div style={{ fontWeight: 700 }}>
                      {coupon.expires_at ? new Date(coupon.expires_at).toLocaleDateString() : 'Never'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
